/*
 * modals.js — vanilla <dialog> modal controller (issue #20, jQuery eviction 14c).
 *
 * Replaces Magnific Popup's runtime with a native <dialog>-based controller that
 * reproduces Magnific's DOM/CSS contract exactly, so the existing .mfp-* CSS in
 * assets/default.css.liquid drives the same visuals with zero visual change.
 *
 * Loaded BEFORE theme.js (theme.js calls theme.modal). No dependencies.
 *
 * Public API — window.theme.modal:
 *   openElement(targetOrSelector, opts) — inline mode: borrow an in-page element
 *       (e.g. #sizechart / #inquiry / #pickupDrawer, each .mfpbox.mfp-hide.mfp-with-anim),
 *       move it into the dialog, restore it on close.
 *   openHtml(htmlString, opts)          — html mode: render a fetched section string
 *       (quickview/quickshop; root carries .qvPopup.mfpbox.mfp-with-anim).
 *   openIframe(url, opts)               — iframe mode (video/custom-block; Phase 2).
 *   close()                             — close the current modal (runs close anim).
 *   normalizeVideoUrl(url)              — watch/short URL -> embed URL helper.
 *
 * opts: { effect, removalDelay, beforeOpen(dlg), onClose() }.
 *   effect       — mainClass on the wrap (default 'mfp-zoom-in'); drives the CSS anim.
 *   removalDelay — ms to wait for the close animation before teardown (default 300).
 *
 * Only one modal is open at a time.
 */
(function () {
  'use strict';

  window.theme = window.theme || {};

  // Critical <dialog> UA-box reset, owned by the controller.
  //
  // A native <dialog> ships UA defaults (white background, solid border, padding,
  // auto width/height) that Magnific's plain <div> wrap never had. Without a reset
  // the modal shows a white bordered box behind the content. The reset also lives in
  // default.css.liquid (added in #20), BUT that file is loaded deferred (media="print"
  // → onload) and, more importantly, a stale committed static assets/default.css can
  // shadow the .liquid on `shopify theme dev`. Injecting the reset here makes the
  // controller self-contained and correct in every environment. Kept minimal —
  // layout/animation still come from the .mfp-* rules in default.css.
  (function injectDialogReset(){
    if (document.getElementById('theme-modal-reset')) return;
    var style = document.createElement('style');
    style.id = 'theme-modal-reset';
    style.textContent =
      'dialog.mfp-wrap{max-width:100%;max-height:100%;margin:0;padding:0;border:0;background:transparent;color:inherit;overflow:visible;}' +
      'dialog.mfp-wrap::backdrop{background:#000;opacity:.5;}';
    (document.head || document.documentElement).appendChild(style);
  })();

  // The currently-open modal state, or null.
  //   { dlg, box, restore, onClose, removalDelay, closing }
  // box = the content box element; clicks outside it (background) close the modal.
  var current = null;

  function makeCloseBtn() {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'mfp-close';
    b.title = 'Close (Esc)';
    b.innerHTML = '&#215;'; // ×
    return b;
  }

  // Build the dialog shell:  <dialog class="mfp-wrap <effect>">
  //                            <div class="mfp-container <holder>">
  //                              <div class="mfp-content"></div>
  // The dialog element itself carries class "mfp-wrap" so the existing
  // .mfp-wrap / .mfp-container / .mfp-content rules and the effect (mainClass)
  // animation selectors apply exactly as they did under Magnific.
  function buildDialog(effect, holder) {
    var dlg = document.createElement('dialog');
    dlg.className = 'mfp-wrap ' + (effect || 'mfp-zoom-in');
    var container = document.createElement('div');
    container.className = 'mfp-container ' + holder;
    var content = document.createElement('div');
    content.className = 'mfp-content';
    container.appendChild(content);
    dlg.appendChild(container);
    dlg._mfpContent = content;
    return dlg;
  }

  function finishClose(state) {
    try { state.dlg.close(); } catch (e) {}
    if (typeof state.restore === 'function') state.restore();
    if (state.dlg.parentNode) state.dlg.parentNode.removeChild(state.dlg);
    if (current === state) current = null;
    if (typeof state.onClose === 'function') state.onClose();
  }

  // close(): run the reverse animation, then tear down after removalDelay.
  // close(true): synchronous teardown (used when replacing an open modal).
  function close(immediate) {
    var state = current;
    if (!state || state.closing) return; // guard double-close
    state.closing = true;
    if (immediate) { finishClose(state); return; }
    state.dlg.classList.add('mfp-removing');
    state.dlg.classList.remove('mfp-ready');
    setTimeout(function () { finishClose(state); }, state.removalDelay);
  }

  function open(dlg, opts, restore, box) {
    // One modal at a time — drop any existing one immediately (no anim).
    if (current) { close(true); }

    var state = {
      dlg: dlg,
      box: box || null,
      restore: restore || null,
      onClose: (opts && opts.onClose) || null,
      removalDelay: (opts && opts.removalDelay) || 300,
      closing: false
    };
    current = state;

    // ESC: <dialog> fires 'cancel' — run our close animation instead of the
    // instant native close (showModal already gives us the focus trap + ESC key).
    dlg.addEventListener('cancel', function (e) { e.preventDefault(); close(); });

    // Close button + background click (Magnific closeOnBgClick default true):
    // any click that is not inside the content box closes the modal.
    dlg.addEventListener('click', function (e) {
      if (e.target.closest('.mfp-close')) { close(); return; }
      if (state.box && state.box.contains(e.target)) { return; }
      close();
    });

    document.body.appendChild(dlg);
    if (opts && typeof opts.beforeOpen === 'function') { opts.beforeOpen(dlg); }

    dlg.showModal(); // top-layer, focus trap, ESC, inert background

    // Double rAF: let the initial (opacity:0 / scale .9) state paint, then add
    // mfp-ready so the transition actually runs.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (current === state) { dlg.classList.add('mfp-ready'); }
      });
    });

    return dlg;
  }

  // ---- inline: borrow an in-page element -------------------------------------
  function openElement(target, opts) {
    opts = opts || {};
    var el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) { return; }

    var dlg = buildDialog(opts.effect, 'mfp-inline-holder');

    // Placeholder marks where the element lives so we can put it back on close.
    var placeholder = document.createComment('mfp-placeholder');
    el.parentNode.insertBefore(placeholder, el);

    el.classList.remove('mfp-hide');
    dlg._mfpContent.appendChild(el);

    // Magnific appends the close button INSIDE the content item, so the theme's
    // `.mfpbox .mfp-close { right:10px; top:10px }` override matches. Keep parity.
    var closeBtn = makeCloseBtn();
    el.appendChild(closeBtn);

    var restore = function () {
      if (closeBtn.parentNode) { closeBtn.parentNode.removeChild(closeBtn); }
      el.classList.add('mfp-hide');
      if (placeholder.parentNode) {
        placeholder.parentNode.insertBefore(el, placeholder);
        placeholder.parentNode.removeChild(placeholder);
      }
    };

    return open(dlg, opts, restore, el);
  }

  // ---- html: render a fetched string (quickview) -----------------------------
  function openHtml(html, opts) {
    opts = opts || {};
    var dlg = buildDialog(opts.effect, 'mfp-inline-holder');
    dlg._mfpContent.innerHTML = html;
    var box = dlg._mfpContent.firstElementChild || dlg._mfpContent;
    var closeBtn = makeCloseBtn();
    box.appendChild(closeBtn); // inside .qvPopup.mfpbox — matches .mfpbox .mfp-close
    // innerHTML does NOT execute <script> tags, but the fetched quickview/quickshop
    // section carries its own scripts (quickview.js, the variant/product-model init,
    // etc.) that must run for the modal to work. Magnific's jQuery insertion used to
    // run them; replicate by re-creating each script node (async=false preserves
    // source order). #20 regressed this by moving to innerHTML; #22 restores it.
    runScripts(dlg._mfpContent);
    return open(dlg, opts, null, box);
  }

  // Re-execute <script> tags inside a freshly-injected subtree: innerHTML parses
  // them but never runs them. Swap each for a fresh <script> node (copying attrs +
  // inline text); external scripts get async=false so they execute in document order.
  function runScripts(root){
    var scripts = root.querySelectorAll('script');
    Array.prototype.forEach.call(scripts, function(old){
      var s = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++){
        s.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      if (old.src){ s.async = false; } else { s.textContent = old.textContent; }
      old.parentNode.replaceChild(s, old);
    });
  }

  // Convert watch/short video URLs to embeddable URLs (Magnific iframe patterns).
  function normalizeVideoUrl(url) {
    if (!url) { return url; }
    if (/\/embed\/|player\.vimeo\.com/.test(url)) { return url; } // already embed
    var m;
    m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
    if (m) { return 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1'; }
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m) { return 'https://player.vimeo.com/video/' + m[1] + '?autoplay=1'; }
    return url;
  }

  // ---- iframe (Phase 2: video / custom-block) --------------------------------
  function openIframe(url, opts) {
    opts = opts || {};
    var dlg = buildDialog(opts.effect, 'mfp-iframe-holder');
    var scaler = document.createElement('div');
    scaler.className = 'mfp-iframe-scaler';
    var iframe = document.createElement('iframe');
    iframe.className = 'mfp-iframe';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; fullscreen');
    iframe.src = normalizeVideoUrl(url);
    scaler.appendChild(iframe);
    dlg._mfpContent.appendChild(scaler);
    // Magnific keeps the iframe close button at the wrap level
    // (`.mfp-iframe-holder .mfp-close { top:-40px }`), not inside the scaler.
    dlg._mfpContent.appendChild(makeCloseBtn());
    return open(dlg, opts, null, scaler);
  }

  theme.modal = {
    openElement: openElement,
    openHtml: openHtml,
    openIframe: openIframe,
    close: close,
    normalizeVideoUrl: normalizeVideoUrl
  };

  // Delegated .mfp binding — replaces the global $('.mfp-link').magnificPopup
  // delegate. Because it lives on document, dynamically-inserted .mfp triggers
  // (e.g. the pickup drawer loaded via ajax) work with no re-registration.
  // Covers: size chart, inquiry link, link-popup blocks, instagram-shop items,
  // pickup drawer, and quick-view's inquiry link — all use `.mfp` + `href="#id"`.
  if (!window.__modalMfpBound) {
    window.__modalMfpBound = true;
    document.addEventListener('click', function (e) {
      // .video_pop: an <a href="video-url"> that opens the video in an iframe modal
      // (folds in the former inline $('.video_pop').magnificPopup({type:'iframe'})
      // inits from video / custom-block / custom-block-masonry). Independent of .mfp.
      var vp = e.target.closest('.video_pop');
      if (vp) {
        e.preventDefault();
        theme.modal.openIframe(vp.getAttribute('href'), { effect: 'mfp-zoom-in', removalDelay: 160 });
        return;
      }
      var a = e.target.closest('.mfp');
      if (!a) { return; }
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) !== '#' || href.length < 2) { return; }
      e.preventDefault();
      theme.modal.openElement(href, {
        effect: a.getAttribute('data-effect') || 'mfp-zoom-in',
        removalDelay: 300,
        beforeOpen: function () {
          document.querySelectorAll('.stickyHeader').forEach(function (el) { el.classList.add('popup'); });
        },
        onClose: function () {
          document.querySelectorAll('.stickyHeader').forEach(function (el) { el.classList.remove('popup'); });
        }
      });
    });
  }
})();
